import React, { useEffect, useState } from 'react';
import './ShopRatingCard.css';
import { useAuth } from '../context/AuthContext';

function ShopRatingCard({ shop, onSelect, detailed = false, onRatingUpdated }) {
  const { user, fetchWithAuth, isAdmin } = useAuth();
  const [ratings, setRatings] = useState(null);
  const [loading, setLoading] = useState(detailed);
  const [editingId, setEditingId] = useState(null);
  const [editStars, setEditStars] = useState(0);
  const [editComment, setEditComment] = useState('');
  const [editAnonymous, setEditAnonymous] = useState(false);

  useEffect(() => {
    if (detailed) {
      fetchRatings();
    }
  }, [shop._id, detailed]);

  const fetchRatings = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/stores/ratings/${shop._id}`);
      if (!res.ok) throw new Error('Failed to fetch ratings');
      const data = await res.json();
      setRatings(data.ratings);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRating = async (ratingId) => {
    if (!window.confirm('Bewertung wirklich löschen?')) return;
    try {
      const res = await fetchWithAuth(`/api/ratings/${ratingId}`, { method: 'DELETE' });
      if (res.ok) {
        fetchRatings();
        if (onRatingUpdated) onRatingUpdated();
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleEditRating = async (ratingId) => {
    try {
      const body = {};
      if (editComment !== undefined) body.comment = editComment;
      if (editStars) body.stars = editStars;
      body.isAnonymous = editAnonymous;

      const res = await fetchWithAuth(`/api/ratings/${ratingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        setEditingId(null);
        setEditComment('');
        setEditStars(0);
        fetchRatings();
        if (onRatingUpdated) onRatingUpdated();
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleCensorRating = async (ratingId, mode) => {
    const label = mode === 'full' ? 'vollständig löschen' : 'zensieren';
    if (!window.confirm(`Bewertung wirklich ${label}?`)) return;
    try {
      const res = await fetchWithAuth(`/api/ratings/${ratingId}/censor`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode })
      });
      if (res.ok) {
        fetchRatings();
        if (onRatingUpdated) onRatingUpdated();
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleCardClick = () => {
    if (!detailed && onSelect) {
      onSelect();
    }
  };

  const renderRating = (value, label, icon, showReviews = false, reviewCount = 0) => {
    if (!value && value !== 0) {
      return (
        <div className="rating-item empty">
          <span className="rating-icon">{icon}</span>
          <div className="rating-content">
            <span className="rating-label">{label}</span>
            <span className="rating-value">-</span>
          </div>
        </div>
      );
    }

    return (
      <div className="rating-item">
        <span className="rating-icon">{icon}</span>
        <div className="rating-content">
          <span className="rating-label">{label}</span>
          <div className="rating-value-group">
            <span className="rating-value">{value.toFixed(1)}</span>
            <span className="rating-stars">
              {'⭐'.repeat(Math.round(value))}
            </span>
          </div>
          {showReviews && <span className="review-count">({reviewCount} Bewertungen)</span>}
        </div>
      </div>
    );
  };

  const dlRating = shop.dlCommunityRating?.average;
  const trustpilotRating = shop.externalRatings?.find(r => r.source === 'trustpilot');
  const trustedShopsRating = shop.externalRatings?.find(r => r.source === 'trustedshops');

  const containerClass = detailed ? 'shop-rating-card detailed' : 'shop-rating-card';
  const clickableClass = !detailed ? 'clickable' : '';

  return (
    <div
      className={`${containerClass} ${clickableClass}`}
      onClick={handleCardClick}
    >
      {/* Header mit Logo und Name */}
      <div className="card-header">
        <div className="logo-section">
          {shop.logo ? (
            <img src={shop.logo} alt={shop.name} className="shop-logo" />
          ) : (
            <div className="shop-logo placeholder">🏪</div>
          )}
        </div>
        <div className="name-section">
          <h3 className="shop-name">{shop.name}</h3>
          {shop.website && (
            <a
              href={shop.website}
              target="_blank"
              rel="noopener noreferrer"
              className="shop-website"
              onClick={(e) => e.stopPropagation()}
            >
              {shop.website}
            </a>
          )}
        </div>
      </div>

      {/* Description */}
      {shop.description && (
        <div className="description">
          {shop.description.substring(0, 150)}
          {shop.description.length > 150 ? '...' : ''}
        </div>
      )}

      {/* Ratings Section */}
      <div className="ratings-container">
        {renderRating(dlRating, 'DL Community', '💜', true, shop.dlCommunityRating?.count)}
        {renderRating(
          trustpilotRating?.rating,
          'Trustpilot',
          '✓',
          true,
          trustpilotRating?.reviewCount
        )}
        {renderRating(
          trustedShopsRating?.rating,
          'Trusted Shops',
          '🏆',
          true,
          trustedShopsRating?.reviewCount
        )}
      </div>

      {/* Shipping Info */}
      {(shop.shippingInfo || shop.returnPolicy) && (
        <div className="info-section">
          {shop.shippingInfo && (
            <p><strong>📦 Versand:</strong> {shop.shippingInfo}</p>
          )}
          {shop.returnPolicy && (
            <p><strong>↩️ Rückgabe:</strong> {shop.returnPolicy}</p>
          )}
        </div>
      )}

      {/* Detailed View Content */}
      {detailed && (
        <div className="detailed-content">
          {loading ? (
            <div className="loading">Bewertungen werden geladen...</div>
          ) : (
            ratings && (
              <div className="reviews-section">
                <h4>Community Bewertungen ({ratings.summary.count})</h4>
                {ratings.items.length === 0 ? (
                  <p className="no-reviews">Noch keine Bewertungen vorhanden. Seien Sie der Erste!</p>
                ) : (
                  <div className="reviews-list">
                    {ratings.items.map(review => {
                      const isOwner = user && String(user.id) === String(review.user?.id);
                      const isMod = isAdmin || (user?.roles?.includes('moderator'));
                      const isEditing = editingId === review._id;

                      return (
                        <div key={review._id} className="review-item">
                          <div className="review-header">
                            <div className="review-user">
                              {review.user?.profileImage && !review.isAnonymous ? (
                                <img 
                                  src={review.user.profileImage} 
                                  alt={review.user.username}
                                  className="review-avatar"
                                />
                              ) : (
                                <div className="review-avatar-placeholder">👤</div>
                              )}
                              <strong className="review-username">
                                {review.isAnonymous ? '🕵️ Anonym' : review.user?.username || 'Unbekannt'}
                              </strong>
                            </div>
                            <div className="review-header-right">
                              <span className="review-rating">
                                {'⭐'.repeat(review.stars)}
                              </span>
                              {/* Edit/Delete Buttons für Owner */}
                              {isOwner && !isEditing && (
                                <div className="review-actions">
                                  <button 
                                    className="review-action-btn edit"
                                    onClick={(e) => { e.stopPropagation(); setEditingId(review._id); setEditStars(review.stars); setEditComment(review.comment || ''); setEditAnonymous(review.isAnonymous || false); }}
                                    title="Bearbeiten"
                                  >✏️</button>
                                  <button 
                                    className="review-action-btn delete"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteRating(review._id); }}
                                    title="Löschen"
                                  >🗑️</button>
                                </div>
                              )}
                              {/* Admin/Mod Buttons */}
                              {isMod && !isOwner && (
                                <div className="review-actions">
                                  <button 
                                    className="review-action-btn censor"
                                    onClick={(e) => { e.stopPropagation(); handleCensorRating(review._id, 'partial'); }}
                                    title="Teilweise zensieren"
                                  >⚠️</button>
                                  <button 
                                    className="review-action-btn censor-full"
                                    onClick={(e) => { e.stopPropagation(); handleCensorRating(review._id, 'full'); }}
                                    title="Vollständig löschen (AGB)"
                                  >🚫</button>
                                  <button 
                                    className="review-action-btn delete"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteRating(review._id); }}
                                    title="Löschen"
                                  >🗑️</button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Edit Form */}
                          {isEditing && isOwner ? (
                            <div className="review-edit-form">
                              <div className="edit-stars">
                                {[1, 2, 3, 4, 5].map(s => (
                                  <button
                                    key={s}
                                    type="button"
                                    className={`star-btn-edit ${editStars >= s ? 'active' : ''}`}
                                    onClick={(e) => { e.stopPropagation(); setEditStars(s); }}
                                  >⭐</button>
                                ))}
                              </div>
                              <textarea
                                value={editComment}
                                onChange={(e) => setEditComment(e.target.value)}
                                maxLength={2000}
                                rows={4}
                                className="edit-textarea"
                                onClick={(e) => e.stopPropagation()}
                              />
                              <label className="edit-anonymous-label" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={editAnonymous}
                                  onChange={(e) => setEditAnonymous(e.target.checked)}
                                />
                                <span>{editAnonymous ? '🕵️ Anonym' : '👤 Mit meinem Namen'}</span>
                              </label>
                              <div className="edit-actions">
                                <button className="edit-save-btn" onClick={(e) => { e.stopPropagation(); handleEditRating(review._id); }}>
                                  ✓ Speichern
                                </button>
                                <button className="edit-cancel-btn" onClick={(e) => { e.stopPropagation(); setEditingId(null); }}>
                                  ✕ Abbrechen
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {review.comment && (
                                <p className="review-text">{review.comment}</p>
                              )}
                            </>
                          )}
                          <span className="review-date">
                            {new Date(review.createdAt).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )
          )}
        </div>
      )}

      {/* Action Button für Card-View */}
      {!detailed && (
        <div className="card-actions">
          <button className="btn-details">
            ▶ Details & Bewertungen
          </button>
        </div>
      )}
    </div>
  );
}

export default ShopRatingCard;
